// Admin: capital donor commitments (NCUA Phase I letter, step 2) with documents + CSV export.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Pencil, Plus, Trash2, UploadCloud, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const errMsg = (e) => formatApiError(e.response?.data?.detail);
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const inputCls = "w-full bg-transparent border border-bdu-gold/25 px-3 py-2.5 text-sm text-bdu-cream outline-none focus:border-bdu-gold";
const TYPES = ["individual", "organization", "church", "business", "foundation"];
const STATUS = {
  prospect: ["Prospect", "border-bdu-cream/30 text-bdu-cream/60"],
  letter_received: ["Letter received", "border-bdu-gold/40 text-bdu-gold"],
  proof_received: ["Proof received", "border-bdu-gold/40 text-bdu-gold"],
  documents_received: ["Documents in", "border-amber-400/50 text-amber-300"],
  verified: ["Verified", "border-emerald-500/50 text-emerald-400"],
};
const DOCS = [["commitment_letter", "Commitment letter", "letter_verified"], ["proof_of_funds", "Proof of funds", "proof_verified"]];
const EMPTY = { name: "", donor_type: "individual", contact_email: "", contact_phone: "", committed_amount: "", notes: "" };

function exportCsv(donors) {
  const head = ["Name", "Type", "Email", "Phone", "Committed Amount", "Status", "Commitment Letter", "Letter Verified", "Proof of Funds", "Proof Verified", "Notes", "Added"];
  const rows = donors.map((d) => [d.name, d.donor_type, d.contact_email || "", d.contact_phone || "", d.committed_amount, STATUS[d.status][0],
    d.docs.commitment_letter?.filename || "", d.docs.commitment_letter?.verified ? "Yes" : "No",
    d.docs.proof_of_funds?.filename || "", d.docs.proof_of_funds?.verified ? "Yes" : "No", d.notes, (d.created_at || "").slice(0, 10)]);
  const csv = [head, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `TDSPFCU_Capital_Donors_${new Date().toISOString().slice(0, 10)}.csv` });
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

function DonorForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...f, committed_amount: Number(f.committed_amount || 0), contact_email: f.contact_email || null, contact_phone: f.contact_phone || null };
    try { await onSave(payload); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border border-bdu-gold/30 bg-bdu-surface p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="capital-donor-form">
      <input required minLength={2} placeholder="Donor name *" value={f.name} onChange={up("name")} className={inputCls} data-testid="capital-donor-name-input" />
      <select value={f.donor_type} onChange={up("donor_type")} className={`${inputCls} bg-bdu-bg capitalize`} data-testid="capital-donor-type-select">
        {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <input type="number" min="0" step="0.01" placeholder="Committed amount ($)" value={f.committed_amount} onChange={up("committed_amount")} className={inputCls} data-testid="capital-donor-amount-input" />
      <input type="email" placeholder="Contact email" value={f.contact_email || ""} onChange={up("contact_email")} className={inputCls} data-testid="capital-donor-email-input" />
      <input placeholder="Contact phone" value={f.contact_phone || ""} onChange={up("contact_phone")} className={inputCls} data-testid="capital-donor-phone-input" />
      <input placeholder="Notes" value={f.notes || ""} onChange={up("notes")} className={inputCls} data-testid="capital-donor-notes-input" />
      <div className="sm:col-span-2 lg:col-span-3 flex gap-3">
        <button type="submit" disabled={busy} className="btn-gold text-xs disabled:opacity-50" data-testid="capital-donor-save-btn">{busy ? "Saving…" : "Save donor"}</button>
        <button type="button" onClick={onCancel} className="btn-ghost text-xs" data-testid="capital-donor-cancel-btn">Cancel</button>
      </div>
    </form>
  );
}

function DocSlot({ donor, kind, label, flag, onChange }) {
  const doc = donor.docs[kind];
  const tid = `capital-donor-${donor.id}-${kind}`;
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("Files must be 25 MB or smaller.");
    const fd = new FormData();
    fd.append("file", file);
    try { onChange((await api.post(`/admin/capital-donors/${donor.id}/documents/${kind}`, fd)).data.donor); toast.success(`${label} uploaded`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const view = async () => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get(doc.url.replace(/^\/api/, ""), { responseType: "blob" });
      w.location.href = URL.createObjectURL(r.data);
    } catch (e) { w?.close(); toast.error("Couldn't open the document."); }
  };
  const verify = async (v) => {
    try { onChange((await api.patch(`/admin/capital-donors/${donor.id}`, { [flag]: v })).data.donor); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="border border-bdu-gold/15 p-3 space-y-2 min-w-0" data-testid={tid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-bdu-cream/55">{label}</div>
      {doc ? (
        <button onClick={view} className="text-xs text-bdu-gold hover:underline inline-flex items-center gap-1.5 max-w-full" data-testid={`${tid}-view`}>
          <FileText size={12} className="shrink-0" /><span className="truncate">{doc.filename}</span>
        </button>
      ) : <div className="text-xs text-bdu-cream/40">Not uploaded</div>}
      <div className="flex items-center justify-between gap-2">
        <label className="text-[10px] uppercase tracking-[0.18em] text-bdu-cream/70 hover:text-bdu-gold cursor-pointer inline-flex items-center gap-1">
          <UploadCloud size={12} /> {doc ? "Replace" : "Upload"}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid={`${tid}-input`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {doc && (
          <label className="text-[10px] uppercase tracking-[0.18em] text-bdu-cream/70 inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={doc.verified} onChange={(e) => verify(e.target.checked)} className="accent-bdu-gold" data-testid={`${tid}-verified`} /> Verified
          </label>
        )}
      </div>
    </div>
  );
}

function DonorCard({ donor, onChange, onEdit, onDelete }) {
  const [label, cls] = STATUS[donor.status];
  return (
    <div className="border border-bdu-gold/15 bg-bdu-surface p-5 grid lg:grid-cols-[1.2fr_1fr_1fr_auto] gap-4 items-start" data-testid={`capital-donor-${donor.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-serif text-xl text-bdu-cream">{donor.name}</span>
          <span className={`text-[0.6rem] uppercase tracking-widest px-2 py-1 border ${cls}`} data-testid={`capital-donor-${donor.id}-status`}>{label}</span>
        </div>
        <div className="mt-1 text-xs text-bdu-cream/55 capitalize">{donor.donor_type}{donor.contact_email ? ` · ${donor.contact_email}` : ""}{donor.contact_phone ? ` · ${donor.contact_phone}` : ""}</div>
        <div className="mt-2 font-serif text-2xl text-bdu-gold tabular-nums" data-testid={`capital-donor-${donor.id}-amount`}>{usd(donor.committed_amount)}</div>
        {donor.notes && <p className="mt-1 text-xs text-bdu-cream/60">{donor.notes}</p>}
      </div>
      {DOCS.map(([kind, l, flag]) => <DocSlot key={kind} donor={donor} kind={kind} label={l} flag={flag} onChange={onChange} />)}
      <div className="flex lg:flex-col gap-3">
        <button onClick={onEdit} className="text-bdu-cream/60 hover:text-bdu-gold" title="Edit" data-testid={`capital-donor-${donor.id}-edit`}><Pencil size={15} /></button>
        <button onClick={onDelete} className="text-bdu-red/70 hover:text-bdu-red" title="Remove" data-testid={`capital-donor-${donor.id}-delete`}><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

export default function CapitalDonorsTab() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/capital-donors").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (editing === "new") await api.post("/admin/capital-donors", payload);
      else await api.patch(`/admin/capital-donors/${editing.id}`, payload);
      toast.success("Donor saved"); setEditing(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.name} from the capital donor list?`)) return;
    try { await api.delete(`/admin/capital-donors/${d.id}`); toast.success("Donor removed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const s = data?.summary;
  return (
    <div className="space-y-6" data-testid="admin-capital-donors-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">NCUA Phase I letter · Step 2 · Seed capital</div>
          <h3 className="mt-2 font-serif text-3xl text-bdu-cream">Capital Donor Tracker</h3>
          <p className="mt-1 text-xs text-bdu-cream/55 max-w-2xl" data-testid="capital-donors-summary">
            {s ? `${s.donors} donors · ${usd(s.committed_total)} committed · ${usd(s.verified_total)} verified (${s.verified_donors} donors) toward the ${usd(s.target)} seed capital.` : "Loading…"}
            {" "}A donor is verified once both the commitment letter and proof of funds are checked.
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => exportCsv(data.donors)} disabled={!data?.donors.length} className="btn-ghost text-xs inline-flex items-center gap-2 disabled:opacity-40" data-testid="capital-donors-export-btn"><Download size={14} /> Export spreadsheet</button>
          <button onClick={() => setEditing("new")} className="btn-gold text-xs" data-testid="capital-donors-add-btn"><Plus size={14} /> Add donor</button>
        </div>
      </div>
      {editing && (
        <div className="relative">
          <button onClick={() => setEditing(null)} className="absolute top-3 right-3 text-bdu-cream/50 hover:text-bdu-gold z-10" aria-label="Close"><X size={16} /></button>
          <DonorForm key={editing.id || "new"} initial={editing === "new" ? EMPTY : { ...EMPTY, ...editing }} onSave={save} onCancel={() => setEditing(null)} />
        </div>
      )}
      {data && !data.donors.length && <div className="border border-bdu-gold/10 py-14 text-center text-bdu-cream/50" data-testid="capital-donors-empty">No capital donors yet. Add the first commitment above.</div>}
      <div className="space-y-3">
        {data?.donors.map((d) => <DonorCard key={d.id} donor={d} onChange={load} onEdit={() => setEditing(d)} onDelete={() => remove(d)} />)}
      </div>
    </div>
  );
}
